import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ImportExcelDialog } from '../components/ImportExcelDialog';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import PoliciesListOptimized from '../components/PoliciesListOptimized';

export const Policies = () => {
  const { user } = useAuth();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/policies/export/csv', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'polizas_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Pólizas exportadas exitosamente');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No hay pólizas para exportar');
      } else {
        toast.error('Error al exportar pólizas');
      }
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Pólizas
          </h1>
          <p className="text-sm leading-relaxed text-slate-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            Gestiona tu cartera de seguros
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-policies-button"
            onClick={handleExportCSV}
            variant="outline"
            className="border-[#10b981] text-[#10b981] hover:bg-green-50 rounded-[10px] font-medium px-6 py-2.5"
          >
            <Download size={20} className="mr-2" />
            Exportar
          </Button>
          <Button
            data-testid="import-xlsx-button"
            onClick={() => setImportDialogOpen(true)}
            variant="outline"
            className="border-[#2537b0] text-[#2537b0] hover:bg-blue-50 rounded-[10px] font-medium px-6 py-2.5"
          >
            <Upload size={20} className="mr-2" />
            Importar Excel
          </Button>
          <Button
            data-testid="create-policy-button"
            onClick={() => navigate('/policies/new')}
            className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white rounded-[10px] font-medium px-6 py-2.5 transition-all shadow-sm hover:shadow-md"
          >
            <Plus size={20} className="mr-2" />
            Nueva Póliza
          </Button>
        </div>
      </div>

      {/* ============================================ */}
      {/* COMPONENTE OPTIMIZADO DE LISTA DE PÓLIZAS */}
      {/* ============================================ */}
      <PoliciesListOptimized />

      {/* 
        NOTA: El componente antiguo ha sido reemplazado por PoliciesListOptimized
        que utiliza el endpoint /api/policies-optimized/list con agregación MongoDB
        para mejor rendimiento y paginación del lado del servidor.
        
        El componente anterior con filtrado en cliente ha sido comentado para
        referencia futura. Si necesitas restaurarlo, busca en git history.
      */}

      {/* Import Excel Dialog */}
      <ImportExcelDialog 
        open={importDialogOpen} 
        onOpenChange={setImportDialogOpen}
        onSuccess={() => window.location.reload()}
        title="Importar Pólizas desde Excel"
        description="Carga un archivo Excel (.xlsx) con los datos de las pólizas para importarlas masivamente"
        downloadTemplateEndpoint="/policies/template/xlsx"
        templateFilename="plantilla_polizas.xlsx"
        uploadEndpoint="/policies/import/xlsx"
        identifierLabel="No. Póliza"
        identifierField="policy_number"
      />
    </div>
  );
};
